<!--
  MinimizedWindowDock.vue
  -----------------------
  Globale Taskbar (unten links) für alle offenen Dialog-Fenster.

  Jedes über `useDialogWindow` geöffnete Fenster erscheint hier als Tab mit
  Icon + Titel. Ein Klick minimiert ein sichtbares Fenster bzw. stellt ein
  minimiertes an seiner letzten Position wieder her. Wird in App.vue einmalig
  eingebunden und rendert nichts, solange keine Fenster offen sind.
-->
<template>
  <Teleport to="body">
    <TransitionGroup
      v-if="dock.windows.length"
      name="dock-chip"
      tag="div"
      class="window-dock"
      role="toolbar"
      :aria-label="$gt('Offene Fenster')"
    >
      <button
        v-for="win in dock.windows"
        :key="win.id"
        type="button"
        class="window-dock__chip"
        :class="{ 'window-dock__chip--active': !win.minimized }"
        :title="win.minimized ? $gt('Fenster wiederherstellen') : $gt('Fenster minimieren')"
        :aria-pressed="!win.minimized"
        @click="win.minimized ? win.restore() : win.minimize()"
      >
        <span class="window-dock__chip-icon">
          <component :is="win.icon" v-if="win.icon" :size="15" />
          <AppWindow v-else :size="15" />
        </span>
        <span class="window-dock__chip-label">{{ $gt(win.title) }}</span>
        <span class="window-dock__chip-restore">
          <ChevronUp v-if="win.minimized" :size="14" />
          <ChevronDown v-else :size="14" />
        </span>
      </button>
    </TransitionGroup>
  </Teleport>
</template>

<script>
import { AppWindow, ChevronUp, ChevronDown } from "lucide-vue-next";
import { useDockWindows } from "@/composables/useDialogWindowDock";

export default {
  name: "MinimizedWindowDock",
  components: { AppWindow, ChevronUp, ChevronDown },
  setup() {
    const dock = useDockWindows();
    return { dock };
  },
};
</script>

<style scoped>
.window-dock {
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 3000;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}

.window-dock__chip {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px 7px 8px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #334155;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12);
  transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.18s ease, border-color 0.18s ease;
}
.window-dock__chip:hover {
  transform: translateY(-2px);
  border-color: #cbd5e1;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
}
.window-dock__chip:focus-visible {
  outline: 2px solid var(--primary-color, #2563eb);
  outline-offset: 2px;
}
.window-dock__chip:active {
  transform: translateY(0);
}

/* Sichtbares (nicht minimiertes) Fenster: blau markierter Taskbar-Tab */
.window-dock__chip--active {
  border-color: var(--primary-color, #2563eb);
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.22);
}
.window-dock__chip--active .window-dock__chip-icon {
  background: var(--primary-color, #2563eb);
  color: #fff;
}

.window-dock__chip-icon {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: #f1f5f9;
  color: var(--primary-color, #2563eb);
  flex: 0 0 auto;
}

.window-dock__chip-label {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.window-dock__chip-restore {
  display: grid;
  place-items: center;
  color: #94a3b8;
}
.window-dock__chip:hover .window-dock__chip-restore {
  color: var(--primary-color, #2563eb);
}

/* Chip-Ein-/Austritt */
.dock-chip-enter-active,
.dock-chip-leave-active {
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s ease;
}
.dock-chip-enter-from,
.dock-chip-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.9);
}
.dock-chip-move {
  transition: transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);
}

@media (prefers-reduced-motion: reduce) {
  .window-dock__chip,
  .dock-chip-enter-active,
  .dock-chip-leave-active,
  .dock-chip-move {
    transition: none;
  }
}
</style>

<!-- Globale Styles für verschieb-/minimierbare Dialog-Fenster (useDialogWindow) -->
<style>
.v-overlay__content.dialog-window .dialog-window-handle {
  cursor: grab;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}

.v-overlay__content.dialog-window.dialog-window--dragging {
  transition: none !important;
  will-change: transform;
}
.v-overlay__content.dialog-window.dialog-window--dragging .dialog-window-handle {
  cursor: grabbing;
}

.v-overlay__content.dialog-window.dialog-window--animating {
  transition:
    transform 0.34s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.26s ease !important;
  will-change: transform, opacity;
}

.v-overlay__content.dialog-window.dialog-window--repositioning {
  transition: transform 0.34s cubic-bezier(0.22, 1, 0.36, 1) !important;
}

.v-overlay__content.dialog-window.dialog-window--hidden {
  visibility: hidden;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .v-overlay__content.dialog-window.dialog-window--animating,
  .v-overlay__content.dialog-window.dialog-window--repositioning {
    transition: none !important;
  }
}
</style>
